import { oc } from "@orpc/contract";
import { z } from "zod";
import { ArInvoiceSalespersonSchema } from "./ar-invoices.contract";

/**
 * AR Customer Profile RPC Contract
 *
 * Provides a single endpoint for fetching all deterministic customer data
 * needed by an AR clerk: aging buckets, open/overdue balances, credit
 * utilization, recent payments, payment terms, contact info, behavioral
 * metrics (avg days to pay, on-time rate, trend), and an activity timeline.
 *
 * All data is computed server-side via SQL aggregates. The aging basis
 * (invoice date, due date, or ship date) is a frontend-configurable parameter.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

/**
 * Input schema for getCustomerProfile — requires a specific customer UUID
 * and an optional aging basis (defaults to invoice date).
 */
export const GetArCustomerProfileInputSchema = z.object({
  customerId: z.string().uuid(),
  agingBasis: z
    .enum(["invoiceDate", "dueDate", "shipDate"])
    .default("shipDate"),
});

/**
 * A single recent payment entry (DATA-04)
 */
export const ArRecentPaymentSchema = z.object({
  id: z.string().uuid(),
  paymentDate: z.string(), // ISO date string
  amount: DecimalStringSchema,
  referenceNumber: z.string().nullable(),
  paymentMethod: z.string().nullable(),
});

/**
 * A single activity timeline entry (ACTV-01)
 */
export const ArActivityTimelineItemSchema = z.object({
  id: z.string(),
  type: z.enum([
    "invoice_created",
    "invoice_sent",
    "statement_sent",
    "payment_received",
    "email_inbound",
    "email_outbound",
    "credit_limit_change",
    "customer_note_added",
    "collection_status_changed",
  ]),
  timestamp: z.string(), // ISO datetime string
  description: z.string(), // human-readable description
  amount: DecimalStringSchema.nullable(), // relevant for invoices/payments
  status: z.string().nullable(), // invoice status, payment status, etc.
  referenceId: z.string().nullable(), // ID to link to detail view
  referenceType: z.string().nullable(), // entity type: "invoice", "payment", "email_thread"
  actorUserName: z.string().nullable().optional(), // name of the user who created the activity
});

/**
 * A single inbound email summary for the customer inbox card.
 */
export const ArCustomerInboundEmailSchema = z.object({
  id: z.string().uuid(),
  subject: z.string().nullable(),
  from: z.string().nullable(),
  receivedAt: z.string(), // ISO datetime
});

/**
 * A single outbound email summary for the customer sent emails section.
 */
export const ArCustomerOutboundEmailSchema = z.object({
  id: z.string().uuid(),
  subject: z.string().nullable(),
  to: z.string().nullable(),
  sentAt: z.string(), // ISO datetime
});

/**
 * Full customer profile response schema covering all 10 requirements
 * (DATA-01 through ACTV-01).
 */
export const ArCustomerProfileResponseSchema = z.object({
  /**
   * Aging buckets and balances (DATA-01 + DATA-02)
   */
  aging: z.object({
    /** Balance not yet due (based on agingBasis parameter) */
    current: DecimalStringSchema,
    /** 1-30 days overdue */
    days1_30: DecimalStringSchema,
    /** 31-60 days overdue */
    days31_60: DecimalStringSchema,
    /** 61-90 days overdue */
    days61_90: DecimalStringSchema,
    /** 90+ days overdue */
    days90plus: DecimalStringSchema,
    /** Sum of all unpaid invoices (all buckets) */
    totalOpen: DecimalStringSchema,
    /** Sum of overdue buckets only (days1_30 + days31_60 + days61_90 + days90plus) */
    totalOverdue: DecimalStringSchema,
    /** Count of open (unpaid) invoices */
    invoiceCount: z.number().int().nonnegative(),
  }),

  /**
   * Credit limit and utilization (DATA-03)
   */
  credit: z.object({
    /** Customer credit limit; null if no limit is set */
    creditLimit: DecimalStringSchema.nullable(),
    /** Current open balance used against the limit */
    utilizationAmount: DecimalStringSchema,
    /** Utilization as a percentage (0-100); null when creditLimit is null or 0 */
    utilizationPct: z.number().nullable(),
    /** false when creditLimit is null */
    hasLimit: z.boolean(),
  }),

  /**
   * Incoming payments — this customer's remittances that have been matched to
   * a payment but not yet confirmed against a bank deposit. Mirrors the AR
   * summary "Incoming Payments" metric, scoped to the customer.
   */
  incomingPayments: z.object({
    /** Sum of stated remittance amounts not yet bank-cleared */
    total: DecimalStringSchema,
    /** Count of remittances contributing to the total */
    count: z.number().int().nonnegative(),
  }),

  /**
   * Last 5 payments (DATA-04)
   */
  recentPayments: z.array(ArRecentPaymentSchema),

  /**
   * Customer payment terms (DATA-05)
   */
  terms: z.object({
    /** Payment terms string from ar_customers (e.g. "Net 30") */
    description: z.string().nullable(),
    /** Payment term days from invoice if available */
    termDays: z.number().int().nullable(),
  }),

  /**
   * Customer contact information (DATA-06)
   */
  contact: z.object({
    contactPerson: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
  }),

  /**
   * Behavioral payment metrics (BEHV-01, BEHV-02, BEHV-03)
   */
  behavioral: z.object({
    /** Avg days from invoice date to fully-paid date; null if no paid invoices in 12-month window */
    avgDaysToPay: z.number().nullable(),
    /** Number of invoices used for the avgDaysToPay calculation ("Based on N invoices" qualifier) */
    invoiceCountUsed: z.number().int(),
    /** On-time payment rate as percentage 0-100; null if no data */
    onTimePaymentRate: z.number().nullable(),
    /** Payment trend direction over trailing 6 months */
    trend: z.enum(["improving", "stable", "declining"]),
    /** Total invoices used for trend calculation (both windows combined) */
    trendInvoiceCount: z.number().int(),
    /** true if customer has no invoice history at all */
    isNewCustomer: z.boolean(),
  }),

  /**
   * Unified activity timeline — last ~10 events across all types (ACTV-01)
   */
  activityTimeline: z.array(ArActivityTimelineItemSchema),

  /**
   * Inbound emails from this customer (matched by email domain)
   */
  inboundEmails: z.array(ArCustomerInboundEmailSchema),

  /**
   * Outbound emails sent to this customer (statements, invoices, etc.)
   */
  outboundEmails: z.array(ArCustomerOutboundEmailSchema),

  /**
   * The customer's primary salesperson. Resolved from
   * `Customer.primarySalesPersonContactId` (an internal Contact row, sourced
   * from Famous at ingest). Null when the customer has no rep configured;
   * `email` may be null when Famous carries a name but no comm record.
   */
  salesperson: ArInvoiceSalespersonSchema.nullable(),
});

/**
 * AR Customer Profile Contracts
 */
export const arCustomerProfileContracts = {
  get: oc
    .input(GetArCustomerProfileInputSchema)
    .output(ArCustomerProfileResponseSchema)
    .route({ method: "GET" }),
} as const;

// Type exports
export type GetArCustomerProfileInput = z.infer<
  typeof GetArCustomerProfileInputSchema
>;
export type ArCustomerProfileResponse = z.infer<
  typeof ArCustomerProfileResponseSchema
>;
export type ArActivityTimelineItem = z.infer<
  typeof ArActivityTimelineItemSchema
>;
export type ArRecentPayment = z.infer<typeof ArRecentPaymentSchema>;
export type ArCustomerInboundEmail = z.infer<
  typeof ArCustomerInboundEmailSchema
>;
export type ArCustomerOutboundEmail = z.infer<
  typeof ArCustomerOutboundEmailSchema
>;
